import PropTypes from 'prop-types'
import { Button, ConfigProvider, Flex, Form, Input } from 'antd'
import { useIntl } from 'react-intl'
import NewPasswordFields from './NewPasswordFields'
import { FORM_THEME } from './theme'

/**
 * First step of a password reset: the email to send a code to.
 */
export const RequestResetForm = ({ busy, initialEmail, onSubmit, onBack }) => {
  const intl = useIntl()

  return (
    <ConfigProvider theme={FORM_THEME}>
      <Form
        id="reset-request-form"
        name="reset-request-form"
        layout="vertical"
        requiredMark={false}
        disabled={busy}
        initialValues={{ email: initialEmail }}
        onFinish={({ email }) => onSubmit(email.trim())}
      >
        <Form.Item
          name="email"
          label={intl.formatMessage({ id: 'auth.emailLabel' })}
          rules={[{ required: true, whitespace: true, message: intl.formatMessage({ id: 'auth.emailRequired' }) }]}
        >
          <Input
            data-testid="email"
            type="email"
            placeholder={intl.formatMessage({ id: 'auth.emailPlaceholder' })}
            autoComplete="username"
            autoFocus
          />
        </Form.Item>
        <Flex vertical={true} gap="small">
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            block
            loading={busy}
            data-testid="reset-request-submit"
          >
            {intl.formatMessage({ id: 'auth.sendCodeButton' })}
          </Button>
          <Flex justify="center">
            <Button type="link" onClick={onBack} data-testid="back-to-sign-in">
              {intl.formatMessage({ id: 'auth.backToSignIn' })}
            </Button>
          </Flex>
        </Flex>
      </Form>
    </ConfigProvider>
  )
}

RequestResetForm.propTypes = {
  busy: PropTypes.bool,
  initialEmail: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
}

/**
 * Second step of a password reset: the emailed code and the new password.
 */
export const ConfirmResetForm = ({ busy, onSubmit, onResend, onBack }) => {
  const intl = useIntl()

  return (
    <ConfigProvider theme={FORM_THEME}>
      <Form
        id="reset-confirm-form"
        name="reset-confirm-form"
        layout="vertical"
        requiredMark={false}
        disabled={busy}
        onFinish={({ reset_code, new_password }) => onSubmit(reset_code.trim(), new_password)}
      >
        <Form.Item
          name="reset_code"
          label={intl.formatMessage({ id: 'auth.resetCodeLabel' })}
          rules={[{ required: true, whitespace: true, message: intl.formatMessage({ id: 'auth.codeRequired' }) }]}
        >
          <Input
            data-testid="reset_code"
            placeholder={intl.formatMessage({ id: 'auth.codePlaceholder' })}
            autoComplete="one-time-code"
            inputMode="numeric"
            autoFocus
          />
        </Form.Item>
        <NewPasswordFields />
        <Flex vertical={true} gap="small">
          <Button type="primary" htmlType="submit" size="large" block loading={busy} data-testid="reset-submit">
            {intl.formatMessage({ id: 'auth.resetPasswordButton' })}
          </Button>
          <Flex justify="center" wrap="wrap">
            <Button type="link" onClick={onResend} data-testid="resend-code">
              {intl.formatMessage({ id: 'auth.resendCode' })}
            </Button>
            <Button type="link" onClick={onBack} data-testid="back-to-sign-in">
              {intl.formatMessage({ id: 'auth.backToSignIn' })}
            </Button>
          </Flex>
        </Flex>
      </Form>
    </ConfigProvider>
  )
}

ConfirmResetForm.propTypes = {
  busy: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onResend: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
}
